import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import ReactImageUploading from "react-images-uploading";
import {ImageListType} from "react-images-uploading/dist/typings";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {scaleImage} from "../../utils/file.utils";
import {SetImagePackSlotCommand} from "../../commands/setImagePackSlot.command";
import {AddImageToPackCommand} from "../../commands/addImageToPack.command";
import {SetImageDisplayNameCommand} from "../../commands/setImageDisplayName.command";
import {makeImageLabelResolver} from "../../utils/imageLabels";

interface ImagePickerProps {
    packId: string;
    level: number;
    variation: number;
    currentKey?: string;     // currently-assigned key, used to highlight selection
    onClose(): void;
}

// Modal launched from the image pack popup when a slot is clicked. Offers
// two paths: upload a brand-new file (existing behaviour, key derived from
// the slot's position), or pick an already-uploaded image key from
// state.images. The pick path is a pure data write — no file traffic —
// which makes re-skinning across entities trivial.
function ImagePicker(props: ImagePickerProps) {
    const {state, executeCommand} = useIntegrator();
    const {uploadFile, getImageUrl, getLocalizationValue} = useApp();
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingKey, setEditingKey] = useState<string | undefined>(undefined);
    const selectedThumbRef = useRef<HTMLDivElement | null>(null);

    // On open, scroll the currently-selected thumbnail into the middle of the
    // visible grid so the user lands looking at "the one they already have".
    // Runs once at mount — subsequent scrolling is the user's to manage.
    useEffect(() => {
        selectedThumbRef.current?.scrollIntoView({block: 'center'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function saveName(key: string, value: string) {
        executeCommand(new SetImageDisplayNameCommand(key, value));
        setEditingKey(undefined);
    }

    // Shared with imagesEditor.view.tsx so the same image renders with the
    // same name in both places.
    const resolver = useMemo(
        () => makeImageLabelResolver(state, getLocalizationValue),
        [state, getLocalizationValue]
    );

    const allKeys = useMemo(() => {
        return Object.keys(state.images ?? {});
    }, [state.images]);

    const labeledKeys = useMemo(() => {
        return allKeys
            .map(key => ({key, ...resolver.labelFor(key)}))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [allKeys, resolver]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return labeledKeys;
        return labeledKeys.filter(({key, label}) =>
            label.toLowerCase().includes(q) || key.toLowerCase().includes(q));
    }, [labeledKeys, search]);

    function pickExisting(key: string) {
        executeCommand(new SetImagePackSlotCommand(props.packId, props.level, props.variation, key));
        props.onClose();
    }

    async function handleUploadChange(value: ImageListType) {
        const file = value[0]?.file;
        if (!file) return;
        // Upload uses the slot-derived key. If the slot already has an image
        // at that key, the upload overwrites the file content.
        const slotKey = `${props.packId}_${props.level}_${props.variation}`;
        scaleImage(file, 256, async (newFile) => {
            setLoading(true);
            const ok = await uploadFile(slotKey, newFile);
            setLoading(false);
            if (ok) {
                // AddImageToPackCommand is idempotent: if the slot already
                // contains a key, it no-ops; if not, it writes the derived
                // key. Either way the pack ends up pointing at slotKey.
                executeCommand(new AddImageToPackCommand(props.packId, props.level, props.variation));
                // If the previous slot key was different (e.g. user had picked
                // an existing key and is now overwriting with a fresh upload),
                // force the slot to the new derived key.
                executeCommand(new SetImagePackSlotCommand(props.packId, props.level, props.variation, slotKey));
                props.onClose();
            }
        });
    }

    return createPortal(
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical gap box'}
                 style={{padding: 15, gap: 10, width: 1040, maxHeight: '85vh', overflow: 'hidden'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Choose Image</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>

                <div className={'layout horizontal gap'} style={{gap: 10, alignItems: 'center'}}>
                    <ReactImageUploading value={[]} onChange={handleUploadChange} maxNumber={1}>
                        {({onImageUpload}) => (
                            <button onClick={onImageUpload} disabled={loading}>
                                <span className={'fa-solid fa-upload'} style={{marginRight: 6}}/>
                                Upload new
                            </button>
                        )}
                    </ReactImageUploading>
                    <input style={{flexGrow: 1, padding: 5}}
                           placeholder={'Search...'}
                           value={search}
                           autoFocus
                           onChange={e => setSearch(e.target.value)}/>
                    <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12}}>
                        {filtered.length} of {allKeys.length}
                    </p>
                </div>

                <div className={'layout horizontal gap'}
                     style={{flexWrap: 'wrap', gap: 4, overflowY: 'auto', paddingRight: 6}}>
                    {filtered.map(({key, label, orphan, explicit}) => {
                        const selected = key === props.currentKey;
                        const tooltipParts: string[] = [];
                        tooltipParts.push(orphan ? `${label} (unused)` : label);
                        tooltipParts.push(key);
                        if (!explicit) tooltipParts.push('(click name to rename)');
                        const tooltip = tooltipParts.join('\n');
                        const isEditing = editingKey === key;
                        return (
                            <div key={key} className={'layout vertical center'} style={{width: 120}}
                                 ref={selected ? selectedThumbRef : undefined}>
                                <div className={`image-cell${selected ? ' selected' : ''}`}
                                     style={{
                                         cursor: 'pointer',
                                         width: 115, height: 115,
                                         opacity: orphan ? 0.6 : 1,
                                     }}
                                     onClick={() => pickExisting(key)}
                                     title={tooltip}>
                                    <img alt={label} src={getImageUrl(key)}
                                         style={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                                </div>
                                {isEditing ? (
                                    <input autoFocus
                                           defaultValue={label}
                                           onFocus={e => e.currentTarget.select()}
                                           onBlur={e => {
                                               const value = e.currentTarget.value;
                                               // If the user pre-filled value was the derived label and
                                               // they didn't change it, don't flip the image to
                                               // "explicitly named" — treat the click as a no-op.
                                               if (!explicit && value === label) {
                                                   setEditingKey(undefined);
                                                   return;
                                               }
                                               saveName(key, value);
                                           }}
                                           onKeyDown={e => {
                                               if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                                               else if (e.key === 'Escape') setEditingKey(undefined);
                                           }}
                                           onClick={e => e.stopPropagation()}
                                           style={{
                                               fontSize: 'var(--font-size-default)',
                                               boxSizing: 'border-box', width: 116,
                                               padding: '1px 2px',
                                               border: '1px solid #515151',
                                               textAlign: 'center',
                                           }}/>
                                ) : (
                                    // Matching border/padding (transparent) keeps the rendered box the
                                    // same size as the input so clicking the label doesn't shift
                                    // rows below it.
                                    <p style={{
                                        fontSize: 'var(--font-size-default)',
                                        boxSizing: 'border-box', maxWidth: 116, width: 116,
                                        padding: '1px 2px',
                                        border: '1px solid transparent',
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        fontStyle: orphan ? 'italic' : 'normal',
                                        fontWeight: explicit ? 600 : 400,
                                        color: selected ? 'var(--tint-color)' : 'rgba(255,255,255,0.66)',
                                        cursor: 'text',
                                    }}
                                       onClick={e => { e.stopPropagation(); setEditingKey(key); }}>
                                        {label}{orphan ? ' •' : ''}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    {filtered.length === 0 &&
                        <p style={{color: 'rgba(255,255,255,0.66)'}}>No matching images.</p>}
                </div>

                {loading && <p style={{color: 'rgba(255,255,255,0.66)'}}>Uploading...</p>}
            </div>
        </div>,
        document.body,
    );
}

export default ImagePicker;
